<template>
  <div>
    <v-row>
      <v-col class="col-12">
        <!-- header -->
        <v-row
          v-if="basketInstance.getBasketEntries().length"
          class="border-bottom"
        >
          <!-- date -->
          <v-col class="col-1 pt-0">
            {{ $t('general.date') }}
          </v-col>

          <!-- product -->
          <v-col class="col-2 pt-0">
            {{ $t('createBooking.productOrEventShort') }}
          </v-col>

          <!-- product definition -->
          <v-col class="col-5 pt-0">
            {{ $t('shopBackendBookingComplete.productDefinition') }}
          </v-col>

          <!-- client -->
          <v-col class="col-2 pt-0">
            {{ $t('shopBackendBookingOverview.customer') }}
          </v-col>

          <!-- price -->
          <v-col class="col-1 pt-0">
            {{ $t('general.price') }}
          </v-col>

          <!-- delete -->
          <v-col class="col-1 pt-0 text-right">
            <!-- {{$t('general.delete')}} -->
          </v-col>
        </v-row>

        <!-- iterate basket entries -->
        <v-row
          v-for="(
            basketEntry, basketEntryIndex
          ) in basketInstance.getBasketEntries()"
          :key="basketEntryIndex"
          class="align-center font-weight-thin border-bottom"
        >
          <!-- date -->
          <v-col class="col-1 py-1">
            {{ basketEntry.getFormatedDate() }}
          </v-col>

          <!-- product or event title -->
          <v-col class="col-2 py-1">
            {{ basketEntry.getTitle() }}
          </v-col>

          <!-- product definition -->
          <v-col class="col-5 py-1">
            {{
              $t(basketEntry.getProductDefinitionInstance().getTranslation())
            }}
          </v-col>

          <!-- client -->
          <v-col class="col-2 py-1">
            <!-- the main user is me (cash desk mode) => prevent capturing shadow users -->
            <span v-if="isSelectedUserMe || bookingInstance">
              {{ selectedUserBookings.getFullName() }}
            </span>

            <v-select
              v-else-if="selectedUserBookings"
              outlined
              :items="selectedUserBookings.getShadowUsersIncludingMe()"
              label=""
              :item-text="(user) => user.getFullName()"
              :item-value="(user) => user.getId()"
              return-object
              dense
              hide-details
              :value="basketEntry.getUserData().getUid()"
              @input="
                (user) => {
                  changeUserInBasketEntry(user, basketEntry)
                }
              "
            >
              <template #append-outer>
                <v-btn
                  icon
                  color="primary"
                  class="mt-n1"
                  @click="$refs.addNewShadowUser.show()"
                >
                  <span class="fal fa-user-plus" />
                </v-btn>
              </template>
            </v-select>
          </v-col>

          <!-- price -->
          <v-col class="col-1 py-1">
            <!-- required prod def ? -->
            <span v-if="basketEntry.isRequiredEntry()">
              {{ $t('createBooking.incl') }}
            </span>
            <span v-else>
              {{ basketEntry.getPrice() | currency }}
            </span>
          </v-col>

          <!-- remove or eventually edit asket entry -->
          <v-col class="text-right py-1">
            <!-- delete button -->
            <v-btn
              icon
              color="red"
              @click="basketInstance.deleteBasketEntry(basketEntry.getId())"
            >
              <span class="fal fa-times" />
            </v-btn>

            <!-- edit button -->
            <v-btn
              v-if="bookingInstance"
              :disabled="basketEntry.isRequiredEntry()"
              icon
              color="primary"
              class="ml-1"
              @click="editBasketEntry(basketEntry)"
            >
              <span v-if="basketEntry.isRequiredEntry()" class="fal fa-link" />
              <span v-else class="fal fa-pen" />
            </v-btn>
          </v-col>
        </v-row>
        <!-- eventually add new basket entry -->
        <v-row v-if="bookingInstance">
          <v-col class="col-12 border-bottom text-right">
            <v-btn outlined color="primary" @click="showAddNewProductModal()">
              <span class="fal fa-plus mr-2" />
              {{ $t('adminBasket.addProduct') }}
            </v-btn>
          </v-col>
        </v-row>

        <!-- group discount -->
        <v-form
          v-if="basketInstance.getBasketEntries().length"
          ref="groupDiscountForm"
          @submit.prevent=""
        >
          <v-row>
            <v-col class="offset-4 col-2 headline-standard mt-4 text-right">
              {{ $t('adminBasket.groupDiscount') }}
            </v-col>

            <!-- Discount type -->
            <v-col class="col-2">
              <v-select
                v-model="discountType"
                :items="['absolute', 'relative']"
                outlined
                :label="$t('adminBasket.discountType')"
                :rules="[(v) => !!v || $t('adminBasket.selectDiscountType')]"
                validate-on-blur
              />
            </v-col>

            <!-- discount description -->
            <v-col class="col-2">
              <v-text-field
                v-model="discountDescription"
                outlined
                :label="$t('adminBasket.discountDescription')"
                :rules="[
                  (v) => !!v || $t('adminBasket.provideDiscountDescription'),
                ]"
                validate-on-blur
              />
            </v-col>

            <!-- discount amount -->
            <v-col class="col-1">
              <v-text-field
                v-model="discountAmount"
                outlined
                type="number"
                :label="$t('v4.amount')"
                :rules="[(v) => !!v || $t('adminBasket.insertDiscountAmount')]"
                validate-on-blur
                @keyup.enter="addGroupDiscount()"
              >
                <template #append-outer>
                  <span v-if="discountType === 'relative'"> % </span>
                </template>
              </v-text-field>
            </v-col>

            <!-- add discount button -->
            <v-col class="col-1 text-right mt-2" @click="addGroupDiscount()">
              <v-btn outlined color="green" large>
                <span class="fal fa-plus fa-2x" />
              </v-btn>
            </v-col>
          </v-row>
        </v-form>

        <!-- prices -->
        <v-row class="headline-standard font-weight-thin">
          <!-- group discounts -->
          <v-col
            v-if="basketInstance.getGroupDiscountsArray().length"
            class="offset-8 col-4 border-bottom py-0 content-4 font-weight-thin"
          >
            <!-- iterate discounts -->
            <v-row
              v-for="(
                groupDiscount, groupDiscountIndex
              ) in basketInstance.getGroupDiscountsArray()"
              :key="groupDiscountIndex"
            >
              <v-col class="col-6 py-0">
                {{ groupDiscount.getDescription() }}
              </v-col>

              <!-- amount -->
              <v-col class="col-4 py-0">
                <!-- absolute -->
                <span v-if="groupDiscount.getType() === 'absolute'">
                  - {{ groupDiscount.getAmount() | currency }}
                </span>
                <span v-else-if="groupDiscount.getType() === 'relative'">
                  - {{ groupDiscount.getAmount() }} %
                </span>
              </v-col>

              <!-- delete group discount -->
              <v-col
                class="col-2 clickable text-right py-0 content-5"
                @click="
                  basketInstance.deleteGroupDiscount(groupDiscount.getId())
                "
              >
                <span class="fal fa-times fa-2x red--text" />
              </v-col>
            </v-row>
          </v-col>

          <!-- cause we care -->
          <v-col
            v-if="basketInstance.causeWeCareActiveInDestination"
            class="offset-8 col-4 border-bottom py-0 content-4 font-weight-thin"
          >
            <v-row class="align-center">
              <v-col class="col-6 py-0">
                <v-checkbox
                  :input-value="basketInstance.causeWeCare"
                  :label="
                    basketInstance.causeWeCare
                      ? $t('adminBasket.withCauseWeCare') + ' (1 %)'
                      : $t('adminBasket.withoutCauseWeCare')
                  "
                  class="py-0"
                  @click="basketInstance.setCauseWeCare()"
                />
              </v-col>
              <v-col class="col-6 py-0">
                <span v-if="basketInstance.causeWeCare">
                  + {{ basketInstance.cwc | currency }}
                </span>

                <span v-else>
                  {{ '0' | currency }}
                </span>
              </v-col>
            </v-row>
          </v-col>

          <!-- vats -->
          <v-col
            v-if="basketInstance.hasVats()"
            class="offset-8 col-4 py-0 border-bottom"
          >
            <!-- total vats -->
            <v-row>
              <v-col class="col-6">
                {{ $t('adminBasket.netWithoutVat') }}
              </v-col>
              <v-col class="col-6">
                {{ basketInstance.getTotalWithoutVats() | currency }}
              </v-col>
            </v-row>

            <!-- iterate vats -->
            <v-row
              v-for="(vat, vatIndex) in basketInstance.getVatsArray()"
              :key="vatIndex"
              class="content-4 font-weight-thin pb-2"
            >
              <v-col class="col-6 py-0">
                {{ $t('adminBasket.vat') }} {{ vat.getRate() }} %
              </v-col>
              <v-col class="col-6 py-0">
                {{ vat.getPrice() | currency }}
              </v-col>
            </v-row>
          </v-col>

          <!-- total -->
          <v-col class="offset-8 col-2 border-bottom font-weight-regular">
            {{ $t('adminBasket.totalInclVat') }}
          </v-col>
          <v-col class="col-2 border-bottom font-weight-regular">
            {{ basketInstance.getPriceNet() | currency }}
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- add new shadow user -->
    <add-new-profile
      v-if="selectedUserBookings"
      ref="addNewShadowUser"
      hide-card-list-switch
      :show-card-list="false"
      :additionalFields="{ mail: '', salutations: salutations }"
      :mandatory-fields="['lastName', 'firstName', 'salutations']"
      shadow
      :foreign-uid-for-shadow-user="selectedUserBookings.getId()"
      @refresh="
        (newShadowUser) => {
          selectedUserBookings.addShadowUser(newShadowUser)
        }
      "
    />

    <!-- modal to add new product -->
    <s-modal ref="addNewProduct" :size="3" hide-buttons fullscreen>
      <create-booking
        ref="createBookingComponent"
        :basketInstance="basketInstance"
        :booking-instance="bookingInstance"
        :selected-user-bookings="selectedUserBookings"
        :booking-states="bookingStates"
        :display-mode="basketEntryToEdit ? 'editProduct' : 'addProduct'"
        @basketEntryAddedOrEdited="closeAddNewProductModal()"
        @cancel="closeAddNewProductModal()"
      />
    </s-modal>
  </div>
</template>

<script>
import Basket from '../../../classes-shared/basket/Basket'
import UserBookings from '../../../classes-shared/bookings/UserBookings'
import AddNewProfile from '../../../components-shared/users/AddNewProfile'
import Salutations from '../../../classes-shared/salutations/Salutations'
import BookingStates from '@/classes-shared/bookings/BookingStates'

export default {
  name: 'AdminBasket',

  components: {
    // required to handle infinite component references
    // https://vuejs.org/v2/guide/components-edge-cases.html#Circular-References-Between-Components
    CreateBooking: () =>
      import('@/components-internal/booking/create/CreateBooking'),
    AddNewProfile,
  },

  props: {
    basketInstance: {
      type: Basket,
      required: true,
    },
    // allow edit basket entries if a booking instance is passed
    bookingInstance: {
      type: Object,
      required: false,
      default: null,
    },
    // the selected user with its bookings and shadow users
    selectedUserBookings: {
      type: UserBookings,
      required: true,
    },
    isSelectedUserMe: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      // for group discount
      discountType: null,
      discountDescription: null,
      discountAmount: null,
      salutations: new Salutations(),
      // data used to make admin basket editable
      bookingStates: new BookingStates(),
      basketEntryToEdit: null,
    }
  },

  methods: {
    changeUserInBasketEntry(user, basketEntry) {
      basketEntry.setUser(user)
      this.basketInstance.updateBasketEntry(basketEntry)
    },
    async addGroupDiscount() {
      if (this.$refs.groupDiscountForm.validate()) {
        await this.basketInstance.addGroupDiscount(
          this.discountType,
          this.discountAmount,
          this.discountDescription
        )

        // reset vars
        this.discountType = null
        this.discountDescription = null
        this.discountAmount = null
      }
    },
    showAddNewProductModal() {
      this.basketEntryToEdit = false
      this.$refs.addNewProduct.show()
      this.$nextTick(() => {
        this.$refs.createBookingComponent.resetPreselection()
      })
    },
    closeAddNewProductModal() {
      this.$refs.addNewProduct.hide()
    },
    editBasketEntry(basketEntry) {
      this.basketEntryToEdit = basketEntry
      this.$refs.addNewProduct.show()
      this.$nextTick(() => {
        this.$refs.createBookingComponent.setBasketEntryForPreselection(
          this.basketEntryToEdit
        )
      })
    },
  },
}
</script>
